import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./context/AuthContext";
import { HelpProvider, HelpPanel } from "@/components/help";
import { ChatProvider } from "@/components/chat/ChatContext";
import { Loader2 } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import SentryUserSync from "@/components/SentryUserSync";

// Critical-path pages: statically imported
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Login from "@/pages/login";
import Signup from "@/pages/signup";
import OnHold from "@/pages/on-hold";
import PrivacyPolicy from "@/pages/privacy-policy";

// Lazy-loaded pages
const News = lazy(() => import("@/pages/news"));
const NewsDetail = lazy(() => import("@/pages/news-detail"));
const Projects = lazy(() => import("@/pages/projects"));
const ProjectDetail = lazy(() => import("@/pages/project-detail"));
const Clients = lazy(() => import("@/pages/clients"));
const Surveys = lazy(() => import("@/pages/surveys"));
const CreateSurvey = lazy(() => import("@/pages/create-survey"));
const TakeSurvey = lazy(() => import("@/pages/take-survey"));
const SurveyResults = lazy(() => import("@/pages/survey-results"));
const Training = lazy(() => import("@/pages/training"));
const TrainingModuleDetails = lazy(() => import("@/pages/training-module-details"));
const PdpDetail = lazy(() => import("@/pages/pdp-detail"));
const Knowledge = lazy(() => import("@/pages/knowledge-projects"));
const KbProjectDetail = lazy(() => import("@/pages/kb-project-detail"));
const KbDocumentEditor = lazy(() => import("@/pages/kb-document-editor"));
const DocumentDrive = lazy(() => import("@/pages/drive"));
const Policies = lazy(() => import("@/pages/policies"));
const Directory = lazy(() => import("@/pages/directory"));
const Timesheet = lazy(() => import("@/pages/timesheet"));
const EffortReport = lazy(() => import("@/pages/effort-report"));
const Tasks = lazy(() => import("@/pages/tasks"));
const MyTasks = lazy(() => import("@/pages/my-tasks"));
const TaskDetail = lazy(() => import("@/pages/task-detail"));
const EditTaskPage = lazy(() => import("@/pages/edit-task"));
const StoryDetail = lazy(() => import("@/pages/story-detail"));
const Admin = lazy(() => import("@/pages/admin"));
const Search = lazy(() => import("@/pages/search"));
const Resources = lazy(() => import("@/pages/resources"));
const ResourcesProjects = lazy(() => import("@/pages/resources-projects"));
const ResourcesProjectDetail = lazy(() => import("@/pages/resources-project-detail"));
const ResourcesUsers = lazy(() => import("@/pages/resources-users"));
const ResourcesUserDetail = lazy(() => import("@/pages/resources-user-detail"));
const ResourcesTeamView = lazy(() => import("@/pages/resources-team-view"));
const ResourcesAvailability = lazy(() => import("@/pages/resources-availability"));
const EffortAnalytics = lazy(() => import("@/pages/effort-analytics"));
const TeamDailyReport = lazy(() => import("@/pages/team-daily-report"));
const AnalyticsHub = lazy(() => import("@/pages/analytics/analytics-hub"));
const TeamUtilization = lazy(() => import("@/pages/analytics/team-utilization"));
const TeamAvailability = lazy(() => import("@/pages/analytics/team-availability"));
const TeamProjectHealth = lazy(() => import("@/pages/analytics/team-project-health"));
const TeamEffortVariance = lazy(() => import("@/pages/analytics/team-effort-variance"));
const TeamTraining = lazy(() => import("@/pages/analytics/team-training"));
const OrgUtilization = lazy(() => import("@/pages/analytics/org-utilization"));
const CrossTeamComparison = lazy(() => import("@/pages/analytics/cross-team-comparison"));
const CostAnalysis = lazy(() => import("@/pages/analytics/cost-analysis"));
const ProjectPortfolio = lazy(() => import("@/pages/analytics/project-portfolio"));
const OrgTrainingSkills = lazy(() => import("@/pages/analytics/org-training-skills"));
const LeaveAttendance = lazy(() => import("@/pages/analytics/leave-attendance"));
const SurveyEngagement = lazy(() => import("@/pages/analytics/survey-engagement"));
const Leave = lazy(() => import("@/pages/leave"));
const WorkLocation = lazy(() => import("@/pages/work-location"));
const RequestAccess = lazy(() => import("@/pages/request-access"));
const LeaveApprovals = lazy(() => import("@/pages/leave-approvals"));
const AdminLeave = lazy(() => import("@/pages/admin-leave"));
const TeamsPage = lazy(() => import("@/pages/admin/teams"));
const AiAnalytics = lazy(() => import("@/pages/admin/ai-analytics"));
const PeopleTeams = lazy(() => import("@/pages/people-teams"));
const TestEmails = lazy(() => import("@/pages/test-emails"));
const ManagerDashboard = lazy(() => import("@/pages/manager-dashboard"));
const Notifications = lazy(() => import("@/pages/notifications"));
const UserProfile = lazy(() => import("@/pages/user-profile"));
const MyProfile = lazy(() => import("@/pages/my-profile"));
const SettingsPage = lazy(() => import("@/pages/settings"));

// Lazy-load ChatWidget (pulls in framer-motion ~25KB)
const ChatWidget = lazy(() => import("@/components/chat/ChatWidget"));

import { useAuth } from "./hooks/useAuth";
import { recordNavigation } from "@/lib/navigationHistory";
import { TourProvider, TourOverlay } from "@/components/tour";
import CommandBar from "@/components/common/CommandBar";
import { VoiceInputProvider } from "@/components/common/VoiceInput";
import { initOneSignal, loginOneSignal, logoutOneSignal } from "@/lib/onesignal";
import AuthLoading from "@/components/auth/AuthLoading";

function OneSignalManager() {
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    initOneSignal();
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loginOneSignal(user.id);
    } else {
      logoutOneSignal();
    }
  }, [isAuthenticated, user?.id]);

  return null;
}

/** Records client-side route changes so useBackNavigation knows history.back() is safe */
function NavigationTracker() {
  const [location] = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // initial page load is not an in-app navigation
    }
    recordNavigation();
  }, [location]);

  return null;
}

function PageLoadingSpinner() {
  return (
    <MainLayout>
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    </MainLayout>
  );
}

function PrivateRoute({ component: Component, ...rest }: any) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [location, setLocation] = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasRedirected) {
      setHasRedirected(true);
      const redirectTo = location !== "/login" && location !== "/" ? `?redirect=${encodeURIComponent(location)}` : "";
      setLocation(`/login${redirectTo}`);
    }
  }, [isAuthenticated, isLoading, hasRedirected]);

  useEffect(() => {
    if (!isLoading && isAuthenticated && user && !user.verified && location !== "/on-hold" && !hasRedirected) {
      setHasRedirected(true);
      setLocation("/on-hold");
    }
  }, [isAuthenticated, isLoading, user, location, hasRedirected]);

  if (isLoading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return null;
  }

  // Redirect unverified users to on-hold page
  if (user && !user.verified) {
    return null;
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <Component {...rest} />
    </Suspense>
  );
}

function Router() {
  return (
    <Switch>
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/on-hold" component={OnHold} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/" component={() => <PrivateRoute component={Dashboard} />} />
        <Route path="/news" component={() => <PrivateRoute component={News} />} />
        <Route path="/news/:id" component={() => <PrivateRoute component={NewsDetail} />} />
        <Route path="/projects" component={() => <PrivateRoute component={Projects} />} />
        <Route path="/projects/:id" component={() => <PrivateRoute component={ProjectDetail} />} />
        <Route path="/clients" component={() => <PrivateRoute component={Clients} />} />
        <Route path="/surveys" component={() => <PrivateRoute component={Surveys} />} />
        <Route path="/create-survey" component={() => <PrivateRoute component={CreateSurvey} />} />
        <Route path="/take-survey/:id" component={() => <PrivateRoute component={TakeSurvey} />} />
        <Route path="/survey-results/:id" component={() => <PrivateRoute component={SurveyResults} />} />
        <Route path="/training" component={() => <PrivateRoute component={Training} />} />
        <Route path="/training/module/:id" component={() => <PrivateRoute component={TrainingModuleDetails} />} />
        <Route path="/pdp/:id" component={() => <PrivateRoute component={PdpDetail} />} />
        <Route path="/knowledge" component={() => <PrivateRoute component={Knowledge} />} />
        <Route path="/knowledge/project/:id" component={() => <PrivateRoute component={KbProjectDetail} />} />
        <Route path="/knowledge/project/:projectId/doc/new" component={() => <PrivateRoute component={KbDocumentEditor} />} />
        <Route path="/knowledge/project/:projectId/doc/:articleId" component={() => <PrivateRoute component={KbDocumentEditor} />} />
        <Route path="/drive" component={() => <PrivateRoute component={DocumentDrive} />} />
        <Route path="/policies" component={() => <PrivateRoute component={Policies} />} />
        <Route path="/my-profile" component={() => <PrivateRoute component={MyProfile} />} />
        <Route path="/directory/user/:userId" component={() => <PrivateRoute component={UserProfile} />} />
        <Route path="/directory" component={() => <PrivateRoute component={Directory} />} />
        <Route path="/timesheet" component={() => <PrivateRoute component={Timesheet} />} />
        <Route path="/effort-report" component={() => <PrivateRoute component={EffortReport} />} />
        <Route path="/tasks" component={() => <PrivateRoute component={Tasks} />} />
        <Route path="/my-tasks" component={() => <PrivateRoute component={MyTasks} />} />
        <Route path="/tasks/task/:taskId/edit" component={() => <PrivateRoute component={EditTaskPage} />} />
        <Route path="/tasks/task/:taskId" component={() => <PrivateRoute component={TaskDetail} />} />
        <Route path="/tasks/story/:id" component={() => <PrivateRoute component={StoryDetail} />} />
        <Route path="/admin" component={() => <PrivateRoute component={Admin} />} />
        <Route path="/people-teams" component={() => <PrivateRoute component={PeopleTeams} />} />
        <Route path="/resources" component={() => <PrivateRoute component={Resources} />} />
        <Route path="/resources/team-view" component={() => <PrivateRoute component={ResourcesTeamView} />} />
        <Route path="/resources/projects" component={() => <PrivateRoute component={ResourcesProjects} />} />
        <Route path="/resources/project/:projectId" component={() => <PrivateRoute component={ResourcesProjectDetail} />} />
        <Route path="/resources/users" component={() => <PrivateRoute component={ResourcesUsers} />} />
        <Route path="/resources/user/:userId" component={() => <PrivateRoute component={ResourcesUserDetail} />} />
        <Route path="/resources/availability" component={() => <PrivateRoute component={ResourcesAvailability} />} />
        <Route path="/analytics" component={() => <PrivateRoute component={AnalyticsHub} />} />
        <Route path="/analytics/effort" component={() => <PrivateRoute component={EffortAnalytics} />} />
        <Route path="/analytics/team-daily" component={() => <PrivateRoute component={TeamDailyReport} />} />
        <Route path="/analytics/team/utilization" component={() => <PrivateRoute component={TeamUtilization} />} />
        <Route path="/analytics/team/availability" component={() => <PrivateRoute component={TeamAvailability} />} />
        <Route path="/analytics/team/project-health" component={() => <PrivateRoute component={TeamProjectHealth} />} />
        <Route path="/analytics/team/effort-variance" component={() => <PrivateRoute component={TeamEffortVariance} />} />
        <Route path="/analytics/team/training" component={() => <PrivateRoute component={TeamTraining} />} />
        <Route path="/analytics/admin/utilization" component={() => <PrivateRoute component={OrgUtilization} />} />
        <Route path="/analytics/admin/cross-team" component={() => <PrivateRoute component={CrossTeamComparison} />} />
        <Route path="/analytics/admin/cost" component={() => <PrivateRoute component={CostAnalysis} />} />
        <Route path="/analytics/admin/portfolio" component={() => <PrivateRoute component={ProjectPortfolio} />} />
        <Route path="/analytics/admin/training-skills" component={() => <PrivateRoute component={OrgTrainingSkills} />} />
        <Route path="/analytics/admin/leave-attendance" component={() => <PrivateRoute component={LeaveAttendance} />} />
        <Route path="/analytics/admin/survey-engagement" component={() => <PrivateRoute component={SurveyEngagement} />} />
        <Route path="/search" component={() => <PrivateRoute component={Search} />} />
        <Route path="/leave" component={() => <PrivateRoute component={Leave} />} />
        <Route path="/work-location" component={() => <PrivateRoute component={WorkLocation} />} />
        <Route path="/request-access" component={() => <PrivateRoute component={RequestAccess} />} />
        <Route path="/leave-approvals" component={() => <PrivateRoute component={LeaveApprovals} />} />
        <Route path="/admin/leave" component={() => <PrivateRoute component={AdminLeave} />} />
        <Route path="/admin/teams" component={() => <PrivateRoute component={TeamsPage} />} />
        <Route path="/admin/ai-analytics" component={() => <PrivateRoute component={AiAnalytics} />} />
        <Route path="/test-emails" component={() => <PrivateRoute component={TestEmails} />} />
        <Route path="/manager-dashboard" component={() => <PrivateRoute component={ManagerDashboard} />} />
        <Route path="/notifications" component={() => <PrivateRoute component={Notifications} />} />
        <Route path="/settings" component={() => <PrivateRoute component={SettingsPage} />} />
        <Route component={NotFound} />
      </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <SentryUserSync />
          <HelpProvider>
            <TourProvider>
              <ChatProvider>
                <VoiceInputProvider>
                  <OneSignalManager />
                  <NavigationTracker />
                  <Toaster />
                  <Router />
                  <HelpPanel />
                  <TourOverlay />
                  <CommandBar />
                  <Suspense fallback={null}>
                    <ChatWidget />
                  </Suspense>
                </VoiceInputProvider>
              </ChatProvider>
            </TourProvider>
          </HelpProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
